import { TargetComparison, type TransformedTarget } from '../../../../../api/targets-client/targets-client.type';
import type { MeasureValuesOverTime } from '../../../../../api/work-periods-client/work-periods-client.type';
import { simpleLinearRegression } from '../../../../../helpers/math-helpers/math-helpers';
import type { RegressionResult } from '../../../../../helpers/math-helpers/math-helpers.type';

/**
 * Sort measure data over time chronologically
 *
 * @param {MeasureValuesOverTime} data - The measure data to sort
 * @returns {MeasureValuesOverTime} - Sorted measure data
 */
const sortMeasureDataOverTime = (data: MeasureValuesOverTime): MeasureValuesOverTime => {
  const sortedEntries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));

  return Object.fromEntries(sortedEntries);
};

/**
 * Calculates the trend of the measure values over time.
 * Uses simple linear regression to determine the trend direction and magnitude.
 *
 * @param {MeasureValuesOverTime} data - The measure data points over time
 * @param {boolean} isZeroValid - Whether zero values should be included in the calculation
 * @returns {RegressionResult | null} - The trend line, or null if insufficient data
 */
const calculateTrend = (data: MeasureValuesOverTime, isZeroValid: boolean): RegressionResult | null => {
  const sortedData = sortMeasureDataOverTime(data);
  const validValues = Object.values(sortedData).filter((value) => value !== null && (isZeroValid || value !== 0));

  if (validValues.length < 2) {
    return null;
  }

  const xAxis = Array.from({ length: validValues.length }, (_, index) => index);
  const line = simpleLinearRegression(xAxis, validValues);

  if (!line) {
    return null;
  }

  return line;
};

/**
 * Returns the appropriate stroke dash array for target lines based on the comparison type
 *
 * @param {TargetComparison} comparison - The target comparison type
 * @returns {string} Stroke dash array for the target line
 */
const getTargetStrokeDashArray = (comparison: TargetComparison): string => {
  switch (comparison) {
    case TargetComparison.GT:
    case TargetComparison.LT:
    case TargetComparison.RANGE_EX:
      return '5 5';
    default:
      return '0 0';
  }
};

/**
 * Prepares target data for chart visualization by transforming time allocations
 *
 * @param {TransformedTarget} target - The target data
 * @param {string[]} dateKeys - The sorted date keys from chart data
 * @returns {Object} Object containing target line data and configuration
 */
const prepareTargetDataForChart = (target: TransformedTarget, dateKeys: string[]) => {
  const isRangeTarget =
    target.target_comparison === TargetComparison.RANGE_IN || target.target_comparison === TargetComparison.RANGE_EX;

  if (isRangeTarget) {
    const lowerData = dateKeys.map((date) => {
      const allocation = target.time_allocations[date];
      return typeof allocation === 'object' && allocation !== null && 'value_lower' in allocation
        ? allocation.value_lower
        : null;
    });

    const upperData = dateKeys.map((date) => {
      const allocation = target.time_allocations[date];
      return typeof allocation === 'object' && allocation !== null && 'value_upper' in allocation
        ? allocation.value_upper
        : null;
    });

    return {
      isRange: true,
      lowerData,
      upperData,
    };
  } else {
    const data = dateKeys.map((date) => {
      const allocation = target.time_allocations[date];
      return typeof allocation === 'number' ? allocation : null;
    });

    return {
      isRange: false,
      data,
    };
  }
};

export { calculateTrend, getTargetStrokeDashArray, prepareTargetDataForChart };
